import Fuse from "fuse.js";

import { EMPTY_ARRAY } from "lib";
import {
  FUSE_OPTIONS,
  formatSearchNameField,
  formatSearchQueryTokens,
  matchSideLoadedBusinessIdentifiers,
  normalizeLower,
  SearchFormattedSideLoadedBusiness,
} from "lib/sideLoadedBusinesses/search";

// Finds the same records as a strict `searchSideLoadedBusinessesWithMatches`
// (ignored and used records included - filter those on the result), but cheaply
// enough to run for every business in a deployment:
//
// - Fuse ANDs a strict query's tokens within a single key, so a record matches
//   a name when some key matches every token. Each distinct token is matched
//   once per key and cached; most names share their words with other names.
// - Before Fuse sees a token, records are narrowed by shared bigrams. A
//   substring within k edits of a token of length L shares at least
//   (L - 1) - 2k of its bigram positions (the q-gram lemma), so the narrowing
//   never drops a record Fuse would match.

export interface SideLoadedSuggestionMatcher {
  matchIds: (name: string) => string[];
}

type BigramIndex = Map<string, number[]>;

const TOKEN_SEARCH_OPTIONS: Fuse.IFuseOptions<SearchFormattedSideLoadedBusiness> =
  { ...FUSE_OPTIONS, includeMatches: true, shouldSort: false };

const SEARCH_KEYS = FUSE_OPTIONS.keys.map(
  key => (key as { name: string }).name,
);

// Fuse's bitap splits patterns longer than this into chunks, which the bigram
// bound doesn't model.
const MAX_NARROWED_TOKEN_LENGTH = 32;

const INCLUDE_MATCH_PREFIX = "'";

// Keeps a bigram from spanning two fields.
const FIELD_SEPARATOR = "\u0000";

function addBigrams(index: BigramIndex, text: string, recordIndex: number) {
  for (let i = 0; i + 1 < text.length; i++) {
    const bigram = text.slice(i, i + 2);
    let postings = index.get(bigram);
    if (!postings) {
      postings = [];
      index.set(bigram, postings);
    }
    if (postings[postings.length - 1] !== recordIndex) {
      postings.push(recordIndex);
    }
  }
}

function intersectSorted(a: number[], b: number[]): number[] {
  const result: number[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      result.push(a[i]);
      i++;
      j++;
    } else if (a[i] < b[j]) {
      i++;
    } else {
      j++;
    }
  }
  return result;
}

// Null when the text is too short to narrow anything.
function recordsWithEveryBigram(
  index: BigramIndex,
  text: string,
): number[] | null {
  if (text.length < 2) {
    return null;
  }
  const postingLists: number[][] = [];
  for (let i = 0; i + 1 < text.length; i++) {
    const postings = index.get(text.slice(i, i + 2));
    if (!postings) {
      return [];
    }
    postingLists.push(postings);
  }
  postingLists.sort((a, b) => a.length - b.length);
  return postingLists.reduce(intersectSorted);
}

export function createSideLoadedSuggestionMatcher(
  records: SideLoadedBusiness[],
): SideLoadedSuggestionMatcher {
  const recordCount = records.length;
  const allRecordIndexes = records.map((_record, i) => i);
  const docs: SearchFormattedSideLoadedBusiness[] = records.map(
    sideLoadedBusiness => ({
      sideLoadedBusiness,
      formattedName: formatSearchNameField(sideLoadedBusiness.name),
    }),
  );

  const nameIndex: BigramIndex = new Map();
  const contactIndex: BigramIndex = new Map();
  const picIndex: BigramIndex = new Map();
  docs.forEach(({ sideLoadedBusiness, formattedName }, i) => {
    addBigrams(
      nameIndex,
      [sideLoadedBusiness.name, formattedName, sideLoadedBusiness.abn]
        .join(FIELD_SEPARATOR)
        .toLowerCase(),
      i,
    );
    addBigrams(
      contactIndex,
      [sideLoadedBusiness.brands, sideLoadedBusiness.email]
        .join(FIELD_SEPARATOR)
        .toLowerCase(),
      i,
    );
    addBigrams(picIndex, sideLoadedBusiness.pics, i);
  });

  const bigramCounts = new Int32Array(recordCount);

  function tokenCandidates(token: string, maxErrors: number): number[] {
    const minSharedBigrams = token.length - 1 - 2 * maxErrors;
    if (minSharedBigrams <= 0 || token.length > MAX_NARROWED_TOKEN_LENGTH) {
      return allRecordIndexes;
    }
    const touched: number[] = [];
    for (let i = 0; i + 1 < token.length; i++) {
      const postings = nameIndex.get(token.slice(i, i + 2)) || EMPTY_ARRAY;
      for (const recordIndex of postings) {
        if (bigramCounts[recordIndex]++ === 0) {
          touched.push(recordIndex);
        }
      }
    }
    const candidates = touched
      .filter(recordIndex => bigramCounts[recordIndex] >= minSharedBigrams)
      .sort((a, b) => a - b);
    touched.forEach(recordIndex => {
      bigramCounts[recordIndex] = 0;
    });
    return candidates;
  }

  const wordCount = Math.ceil(recordCount / 32);
  // Per search key, a bitset of the records matching the token in that key.
  const tokenMatchCache = new Map<string, Uint32Array[]>();

  function matchToken(formattedToken: string): Uint32Array[] {
    const cacheKey = formattedToken.toLowerCase();
    const cached = tokenMatchCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const isIncludeMatch = cacheKey.startsWith(INCLUDE_MATCH_PREFIX);
    const token = isIncludeMatch ? cacheKey.slice(1) : cacheKey;
    const maxErrors = isIncludeMatch
      ? 0
      : Math.floor(token.length * FUSE_OPTIONS.threshold);

    const candidates = tokenCandidates(token, maxErrors);
    const matchesByKey = SEARCH_KEYS.map(() => new Uint32Array(wordCount));
    if (candidates.length) {
      new Fuse(
        candidates.map(recordIndex => docs[recordIndex]),
        TOKEN_SEARCH_OPTIONS,
      )
        .search(formattedToken)
        .forEach(result => {
          const recordIndex = candidates[result.refIndex];
          result.matches.forEach(match => {
            matchesByKey[SEARCH_KEYS.indexOf(match.key)][recordIndex >>> 5] |=
              1 << (recordIndex & 31);
          });
        });
    }
    tokenMatchCache.set(cacheKey, matchesByKey);
    return matchesByKey;
  }

  function addFuzzyMatches(name: string, ids: Set<string>) {
    const tokenMatches = formatSearchQueryTokens(name).map(matchToken);
    if (!tokenMatches.length) {
      return;
    }
    for (let word = 0; word < wordCount; word++) {
      let matchedInAnyKey = 0;
      for (let key = 0; key < SEARCH_KEYS.length; key++) {
        let matchedEveryToken = 0xffffffff;
        for (const matchesByKey of tokenMatches) {
          matchedEveryToken &= matchesByKey[key][word];
          if (!matchedEveryToken) {
            break;
          }
        }
        matchedInAnyKey |= matchedEveryToken;
      }
      while (matchedInAnyKey) {
        const lowestBit = matchedInAnyKey & -matchedInAnyKey;
        ids.add(records[word * 32 + 31 - Math.clz32(lowestBit)].id);
        matchedInAnyKey ^= lowestBit;
      }
    }
  }

  // A superset of the records `matchSideLoadedBusinessIdentifiers` can match,
  // mirroring the normalization it applies to the query.
  function identifierCandidates(name: string): SideLoadedBusiness[] {
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      return EMPTY_ARRAY;
    }
    if (/^\d+$/.test(trimmedName.replace(/\s/g, ""))) {
      return records;
    }
    const picQuery = trimmedName.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const contactCandidates = recordsWithEveryBigram(
      contactIndex,
      normalizeLower(trimmedName),
    );
    const picCandidates = picQuery
      ? recordsWithEveryBigram(picIndex, picQuery)
      : EMPTY_ARRAY;
    if (contactCandidates === null || picCandidates === null) {
      return records;
    }
    return Array.from(new Set([...contactCandidates, ...picCandidates])).map(
      recordIndex => records[recordIndex],
    );
  }

  return {
    matchIds: name => {
      const ids = new Set<string>();
      addFuzzyMatches(name, ids);
      matchSideLoadedBusinessIdentifiers(
        identifierCandidates(name),
        name,
      ).forEach(match => ids.add(match.sideLoadedBusiness.id));
      return ids.size ? Array.from(ids) : EMPTY_ARRAY;
    },
  };
}

// Ignored and already-linked records aren't work waiting to be done, matching
// the suggestion count the business form shows.
export const countNewSideLoadedSuggestions = (
  recordIds: string[],
  recordsById: Record<string, SideLoadedBusiness>,
): number =>
  recordIds.filter(recordId => {
    const record = recordsById[recordId];
    return record && !record.isIgnored && !record.businessIds.length;
  }).length;
